/*
 * PIN des Servicezugangs am Bedienfeld (docs/servicezugang.md), eine je
 * Instanz. Die PIN gibt am Panel bis zur Ebene Fachmann frei, der
 * Servicestick bis OEM (Patrick, 22.10.2026). Der Server speichert nur einen
 * Hash mit Salz; die Panels holen ihn ueber ihr Zertifikat und pruefen damit
 * auch ohne Netz. Recht users.admin.
 */

import { useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import './PlantUsersPage.css';

interface PinState {
  gesetzt: boolean;
  version: number;
  geaendertAm: string | null;
  geaendertVon: string | null;
}

export function ServicePinPage() {
  const [tick, setTick] = useState(0);
  const [pin, setPin] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const state = useAsync((signal) => api.get<PinState>('/api/service-access/pin', signal), [tick]);

  const save = async () => {
    if (pin !== repeat) {
      showToast({ tone: 'error', title: 'Die beiden Eingaben unterscheiden sich.' });
      return;
    }
    setBusy(true);
    try {
      await api.put('/api/service-access/pin', { pin });
      showToast({ tone: 'success', title: 'PIN gesetzt', message: 'Die Panels holen sie binnen zehn Minuten.' });
      setPin('');
      setRepeat('');
      setTick((t) => t + 1);
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gesetzt',
        message: e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? `HTTP ${e.status}`) : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('PIN entfernen? Dann gibt am Panel nur noch der Servicestick frei, offene PIN-Sitzungen enden.')) return;
    try {
      await api.delete('/api/service-access/pin');
      setTick((t) => t + 1);
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht entfernt', message: e instanceof ApiError ? `HTTP ${e.status}` : String(e) });
    }
  };

  const digitsOnly = (v: string) => v.replace(/\D/g, '').slice(0, 8);
  return (
    <AppShell title="Servicezugang">
      <div class="hv-plantusers">
        <p class="hv-plantusers__hint">
          Mit dieser PIN öffnet ein Techniker am Bedienfeld den Servicezugang bis zur Ebene Fachmann: alle Werte
          des Reglers lesen und bis Fachmann ändern. Mit dem Servicestick geht es bis OEM. Die PIN gilt für alle
          Panels dieses Servers. Gespeichert wird sie nicht, nur ein Prüfwert; vergessen heißt neu setzen.
        </p>
        <section class="hv-plantusers__add">
          <h3>Stand</h3>
          {state.status === 'loading' && <p class="muted">Lade…</p>}
          {state.status === 'error' && <p class="hv-plantusers__warn">Nicht ladbar. Dafür braucht es die Berechtigung „users.admin“.</p>}
          {state.status === 'ok' && (state.data.gesetzt
            ? <p>PIN gesetzt, Fassung {state.data.version}, zuletzt geändert {state.data.geaendertAm ? new Date(state.data.geaendertAm).toLocaleString('de-AT') : ''}{state.data.geaendertVon ? ` von ${state.data.geaendertVon}` : ''}.</p>
            : <p>Keine PIN gesetzt. Am Panel gibt nur der Servicestick frei.</p>)}
        </section>
        <section class="hv-plantusers__add">
          <h3>{state.status === 'ok' && state.data.gesetzt ? 'PIN ändern' : 'PIN setzen'}</h3>
          <div class="hv-plantusers__row">
            <input type="password" inputMode="numeric" autoComplete="new-password" placeholder="Neue PIN, 4 bis 8 Ziffern"
                   value={pin} onInput={(e) => setPin(digitsOnly((e.target as HTMLInputElement).value))} />
            <input type="password" inputMode="numeric" autoComplete="new-password" placeholder="Wiederholen"
                   value={repeat} onInput={(e) => setRepeat(digitsOnly((e.target as HTMLInputElement).value))} />
            <button type="button" class="hv-btn hv-btn--primary" disabled={busy || pin.length < 4 || repeat.length < 4}
                    onClick={() => void save()}>
              {busy ? 'Setze…' : 'Setzen'}
            </button>
          </div>
          <p class="muted">Keine lauter gleichen Ziffern und keine einfache Folge wie 1234.</p>
          {state.status === 'ok' && state.data.gesetzt && (
            <button type="button" class="hv-btn" onClick={() => void remove()}>PIN entfernen</button>
          )}
        </section>
      </div>
    </AppShell>
  );
}
